/**
 * The sheet strip above the Tables tab's grid, drawn like the workbook
 * viewer's own sheet tabs so the page reads as their data in a familiar shape.
 */

import { Info } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { SHEETS, type SheetId } from "./sheets";

export interface SheetTabsProps {
	active: SheetId;
	onSelect: (sheet: SheetId) => void;
	/** What the sheet on screen is, behind the ⓘ at the strip's end. */
	about?: string;
}

/** The ⓘ: opens on hover, focus or click, closes on Escape or a click away. */
const About: React.FC<{ text: string }> = ({ text }) => {
	const [open, setOpen] = useState(false);
	const root = useRef<HTMLDivElement>(null);
	useEffect(() => {
		if (!open) return;
		const away = (event: MouseEvent) => {
			if (!root.current?.contains(event.target as Node)) setOpen(false);
		};
		const escape = (event: KeyboardEvent) => event.key === "Escape" && setOpen(false);
		document.addEventListener("mousedown", away);
		document.addEventListener("keydown", escape);
		return () => {
			document.removeEventListener("mousedown", away);
			document.removeEventListener("keydown", escape);
		};
	}, [open]);
	return (
		<div
			ref={root}
			className="relative flex-shrink-0"
			onMouseEnter={() => setOpen(true)}
			onMouseLeave={() => setOpen(false)}
		>
			<button
				type="button"
				aria-label="About this sheet"
				aria-expanded={open}
				onClick={() => setOpen((value) => !value)}
				onFocus={() => setOpen(true)}
				onBlur={() => setOpen(false)}
				className={`inline-flex h-7 w-7 items-center justify-center rounded-md text-[#6B7280] hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#008782] ${open ? "bg-gray-200" : ""}`}
			>
				<Info className="h-4 w-4" aria-hidden />
			</button>
			{open && (
				<div
					role="tooltip"
					className="absolute right-0 top-full z-20 mt-1.5 w-80 rounded-lg border border-gray-200 bg-white px-3.5 py-3 text-[13px] leading-relaxed text-[#1F2937] shadow-lg"
				>
					{text}
				</div>
			)}
		</div>
	);
};

const SheetTabs: React.FC<SheetTabsProps> = ({ active, onSelect, about }) => (
	<div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2">
		<div role="tablist" aria-label="Sheets" className="flex min-w-0 flex-1 gap-1 overflow-x-auto">
			{SHEETS.map((sheet) => (
				<button
					key={sheet.id}
					type="button"
					role="tab"
					aria-selected={sheet.id === active}
					onClick={() => onSelect(sheet.id)}
					className={`whitespace-nowrap rounded-md px-3 py-1 text-xs ${
						sheet.id === active ? "bg-teal-600 text-white" : "text-gray-600 hover:bg-gray-200"
					}`}
				>
					{sheet.label}
				</button>
			))}
		</div>
		{about && <About text={about} />}
	</div>
);

export default SheetTabs;
